/**
 * DocumentQABlock — Astor document Q&A surface (Documents Library)
 *
 * Bottom-of-page panel that pitches the document Q&A feature: ask Astor
 * anything about any uploaded document. Includes 4 pill prompt chips
 * that pre-fill the question, and a "Try it" CTA.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Astor surface 2)
 * - id-bg accent border + square icon badge + title + subtitle + prompts + CTA.
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() for tinted border (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on every chip + CTA (portals.md)
 *
 * TODO(#consumer-doc-qa-route): wire `onTryIt` and `onPromptClick` to a
 *   real chat surface (e.g. `/documents/qa?prompt=...`). Today the parent
 *   passes a stub that logs the intent; the actual route is a follow-up.
 */

import EastIcon from '@mui/icons-material/East';
import MessageIcon from '@mui/icons-material/Message';
import {
  alpha,
  Box,
  baseColors,
  GlassButton,
  GlassChip,
  iconSizes,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';

export interface DocumentQABlockProps {
  onTryIt: () => void;
  onPromptClick: (promptKey: string) => void;
}

const PROMPT_KEYS = [
  'consumer.documents.astorQaPrompt1',
  'consumer.documents.astorQaPrompt2',
  'consumer.documents.astorQaPrompt3',
  'consumer.documents.astorQaPrompt4',
] as const;

export function DocumentQABlock({ onTryIt, onPromptClick }: DocumentQABlockProps) {
  const { t } = useI18n();
  return (
    <Box
      role="region"
      aria-label={t('consumer.documents.astorQaTitle')}
      sx={{
        p: 2.5,
        borderRadius: 1,
        border: 1,
        borderColor: alpha(baseColors.indigo[400], 0.4),
        bgcolor: alpha(baseColors.indigo[400], 0.06),
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', mb: 2 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: alpha(baseColors.indigo[400], 0.16),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MessageIcon sx={{ fontSize: iconSizes.md, color: 'primary.main' }} />
        </Box>
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
            {t('consumer.documents.astorQaTitle')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('consumer.documents.astorQaSubtitle')}
          </Typography>
        </Stack>
        <GlassButton variant="text" endIcon={<EastIcon />} onClick={onTryIt} sx={{ minHeight: 44, flexShrink: 0 }}>
          {t('consumer.documents.astorQaCta')}
        </GlassButton>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {PROMPT_KEYS.map((promptKey) => (
          <GlassChip
            key={promptKey}
            label={t(promptKey)}
            size="small"
            clickable
            onClick={() => onPromptClick(promptKey)}
            sx={{ minHeight: 44 }}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default DocumentQABlock;
